import { NgModule } from '@angular/core';
import { Routes, Route, RouterModule, PreloadingStrategy } from '@angular/router';

import { Observable, of } from 'rxjs';

import { AuthGuard } from 'modules/shared/guards/auth.guards';
import { EnforcedMFAGuard } from 'modules/shared/guards/enforced-mfa.guard';
import { AdminRoleGuard } from 'modules/shared/guards/admin-role.guards';
import { OnlyAdminRoleGuard } from 'modules/shared/guards/only-admin-role.guards';
import { HandshakesEnabledGuard } from 'modules/handshakes/guards/handshakes-enabled.guard';
import { OpenCorporatesEnabledGuard } from 'modules/opencorporates/opencorporates-enabled.guard';

import { LayoutComponent } from 'modules/core/components/layout/layout.component';

const routes: Routes = [
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard, EnforcedMFAGuard],
    children: [
      {
        path: 'admin',
        loadChildren: () => import('modules/admin/admin.module').then(m => m.AdminModule),
        canActivate: [AdminRoleGuard]
      }, {
        path: 'user-guide',
        loadChildren: () => import('modules/user-guide/user-guide.module').then(m => m.UserguideModule),
      }, {
        path: 'customers',
        loadChildren: () => import('modules/customers/customers.module').then(m => m.CustomersModule),
        data: {preload: true},
        canActivate: [OnlyAdminRoleGuard]
      }, {
        path: 'dashboard',
        loadChildren: () => import('modules/dashboard/dashboard.module').then(m => m.DashboardModule),
        canActivate: [OnlyAdminRoleGuard]
      }, {
        path: 'expiring-documents',
        loadChildren: () => import('modules/expiring-documents/expiring-documents.module').then(m => m.ExpiringDocumentsModule),
        canActivate: [OnlyAdminRoleGuard]
      }, {
        path: 'own-restricted-list',
        loadChildren: () => import('modules/own-restricted-list/own-restricted-list.module').then(m => m.OwnRestrictedListModule),
        canActivate: [OnlyAdminRoleGuard]
      }, {
        path: 'account-settings',
        loadChildren: () => import('modules/account-settings/account-settings.module').then(m => m.AccountSettingsModule),
      }, {
        path: 'quick-scan',
        loadChildren: () => import('modules/quick-scan/quick-scan.module').then(m => m.QuickScanModule),
        canActivate: [OnlyAdminRoleGuard]
      }, {
        path: 'xero',
        loadChildren: () => import('modules/xero/xero.module').then(m => m.XeroModule),
        canActivate: [OnlyAdminRoleGuard]
      }, {
        path: 'ongoing-due-diligence',
        loadChildren: () => import('modules/ongoing-due-diligence/ongoing-due-diligence.module').then(m => m.OngoingDueDiligenceModule),
        canActivate: [OnlyAdminRoleGuard]
      }, {
        path: 'handshakes',
        loadChildren: () => import('modules/handshakes/handshakes.module').then(m => m.HandshakesModule),
        canActivate: [OnlyAdminRoleGuard, HandshakesEnabledGuard]
      }, {
        path: 'opencorporates',
        loadChildren: () => import('modules/opencorporates/opencorporates.module').then(m => m.OpenCorporatesModule),
        canActivate: [OnlyAdminRoleGuard, OpenCorporatesEnabledGuard]
      }, {
        path: 'periodic-review',
        loadChildren: () => import('modules/periodic-review/periodic-review.module').then(m => m.PeriodicReviewModule),
        canActivate: [OnlyAdminRoleGuard]
      }, {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      }
    ]
  }
];

export class CustomPreloadingStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (route.data && route.data['preload']) {
      return load();
    } else {
      return of(null);
    }
  }
}

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      anchorScrolling: 'enabled',
      preloadingStrategy: CustomPreloadingStrategy
    })
  ],
  exports: [RouterModule],
  providers: [
    CustomPreloadingStrategy
  ]
})
export class AppRoutingModule { }
